import { Form, Formik } from "formik";
import { Copy, Plus, Trash, X, Search, UserRound } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";
import * as Yup from "yup";
import SideDrawer from "@/components/ui/SideDrawer";
import InputField from "@/components/form/InputField";
import DropdownMenu from "@/components/ui/DropdownMenu";
import { useRoles, useUsersPage } from "@/hooks/useCommonQueries";
import { useDebounce } from "@/hooks/useDebounce";
import { userService } from "@/services/breeze/UserService";
import {
  CreateUserRequest,
  UserSort,
  UsersPageQuery,
  UserWithRole,
} from "@/types/breeze/User";
import SelectField from "@/components/form/SelectField";
import withPermissionRequired from "@/hoc/withPermissionRequired";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import {
  Button,
  Flex,
  Box,
  Heading,
  Text,
  Separator,
  Badge,
  TextField,
  Select,
} from "@radix-ui/themes";
import {
  integrationIcons,
  integrationBrandColors,
} from "@/components/workflow/IntegrationSelector";
import PageTemplate from "@/components/page-template/PageTemplate";
import { Skeleton, SkeletonPage } from "@/components/ui/Skeleton";
import UserManagementTabs from "@/components/users/UserManagementTabs";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import UserAccessSettings from "./UserAccessSettings";
import UserReferralCodeCell from "@/components/anythinkpay/UserReferralCodeCell";
import { useUsersReferralCodes } from "@/hooks/useAnythinkPayOffers";

type ChipKey =
  | "subscribers"
  | "native_signin"
  | "password_signin"
  | "pending_invites"
  | "new_this_week";

const CHIPS: { key: ChipKey; label: string }[] = [
  { key: "subscribers", label: "Has subscriptions" },
  { key: "native_signin", label: "Native sign-in" },
  { key: "password_signin", label: "Password sign-in" },
  { key: "pending_invites", label: "Pending invites" },
  { key: "new_this_week", label: "New this week" },
];

const OPPOSITE_SIGN_IN: Partial<Record<ChipKey, ChipKey>> = {
  native_signin: "password_signin",
  password_signin: "native_signin",
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const PROJECT_PAGE_SIZES = [25, 50, 100];
const CENTRAL_PAGE_SIZE = 100;

// The server pages and sorts, so a header sort or filter would only act on the visible page.
const withoutLocalSortAndFilter = <T,>(columns: DataGridColumn<T>[]) =>
  columns.map((column) => ({ ...column, sortable: false, filter: false }));

const UsersPage = withPermissionRequired(
  "anythink_users:read",
  function UsersPage() {
    const { currentTenant, safeNavigate } = useAuth();
    const [isUsersDrawerOpen, setIsUsersDrawerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const search = useDebounce(searchTerm.trim(), 300);
    const [activeChips, setActiveChips] = useState<Set<ChipKey>>(new Set());
    const [sortKey, setSortKey] = useState<UserSort>("newest");
    const [pageSize, setPageSize] = useState(PROJECT_PAGE_SIZES[0]);
    const [centralPage, setCentralPage] = useState(1);
    const [selectedUsers, setSelectedUsers] = useState<UserWithRole[]>([]);
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    const {
      data: roles,
      isLoading: isRolesLoading,
      error: rolesError,
    } = useRoles();

    const newThisWeek = activeChips.has("new_this_week");
    const createdAfter = useMemo(
      () => (newThisWeek ? new Date(Date.now() - WEEK_MS).toISOString() : undefined),
      [newThisWeek],
    );
    const filters: Omit<UsersPageQuery, "page"> = {
      central: false,
      page_size: pageSize,
      search: search || undefined,
      has_subscriptions: activeChips.has("subscribers") || undefined,
      sign_in: activeChips.has("native_signin")
        ? "native"
        : activeChips.has("password_signin")
          ? "password"
          : undefined,
      pending: activeChips.has("pending_invites") || undefined,
      created_after: createdAfter,
      sort: sortKey,
    };

    const filtersKey = JSON.stringify(filters);
    const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
    const page = pageState.key === filtersKey ? pageState.page : 1;
    const setPage = useCallback(
      (next: number) => setPageState({ key: filtersKey, page: next }),
      [filtersKey],
    );

    const projectQuery = useUsersPage({ ...filters, page });
    const centralQuery = useUsersPage({
      central: true,
      sort: "alphabetical",
      page: centralPage,
      page_size: CENTRAL_PAGE_SIZE,
    });
    const projectPage = projectQuery.data;
    const centralUsersPage = centralQuery.data;

    const createUser = useMutation({
      mutationFn: (user: CreateUserRequest) => userService.createUser(user),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        setIsUsersDrawerOpen(false);
      },
    });

    const deleteUser = useMutation({
      mutationFn: (userId: number) => userService.deleteUser(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
      },
    });

    const bulkDeleteUsers = useMutation({
      mutationFn: async (userIds: number[]) => {
        // No bulk endpoint server-side yet; allSettled keeps a partial failure from
        // hiding successes from the toast and lets the cache invalidate everything.
        const results = await Promise.allSettled(
          userIds.map((id) => userService.deleteUser(id)),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        return { total: userIds.length, failed };
      },
      onSuccess: ({ total, failed }) => {
        const ok = total - failed;
        if (failed === 0) {
          toast.success(`Deleted ${ok} user${ok === 1 ? "" : "s"}`);
        } else if (ok === 0) {
          toast.error(`Failed to delete ${failed} user${failed === 1 ? "" : "s"}`);
        } else {
          toast.error(`Deleted ${ok}, failed ${failed}`);
        }
        setSelectedUsers([]);
        queryClient.invalidateQueries({ queryKey: ["users"] });
      },
    });

    const toggleChip = useCallback((key: ChipKey) => {
      setActiveChips((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else {
          next.add(key);
          const opposite = OPPOSITE_SIGN_IN[key];
          if (opposite) next.delete(opposite);
        }
        return next;
      });
    }, []);

    const clearAllFilters = useCallback(() => {
      setActiveChips(new Set());
      setSearchTerm("");
    }, []);

    const handleBulkDelete = useCallback(async () => {
      if (selectedUsers.length === 0) return;
      const confirmed = await confirm({
        title: `Delete ${selectedUsers.length} user${selectedUsers.length === 1 ? "" : "s"}?`,
        message:
          selectedUsers.length === 1
            ? `Are you sure you want to delete ${selectedUsers[0].first_name} ${selectedUsers[0].last_name}?`
            : `Are you sure you want to delete these ${selectedUsers.length} users? This cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "danger",
      });
      if (!confirmed) return;
      bulkDeleteUsers.mutate(selectedUsers.map((u) => u.id));
    }, [selectedUsers, confirm, bulkDeleteUsers]);

    useEffect(() => {
      setSelectedUsers((prev) => (prev.length ? [] : prev));
    }, [projectPage?.items]);

    useEffect(() => {
      if (projectPage && projectPage.items.length === 0 && page > 1)
        setPage(Math.max(1, projectPage.total_pages));
    }, [projectPage, page, setPage]);

    const projectUserIds = useMemo(
      () => projectPage?.items.map((u) => u.id) ?? [],
      [projectPage],
    );
    const referralCodesQuery = useUsersReferralCodes(projectUserIds);

    if (isRolesLoading || projectQuery.isLoading || centralQuery.isLoading) {
      return <SkeletonPage title="User Management" />;
    }

    if (rolesError || projectQuery.error || centralQuery.error || !projectPage || !centralUsersPage) {
      return <div>Error loading users</div>;
    }

    const hasFilters = activeChips.size > 0 || searchTerm.trim() !== "";

    // Column definitions for central users (read-only, no actions)
    const centralUsersColumnDefs: DataGridColumn<UserWithRole>[] = [
      {
        field: "first_name",
        header: "Name",
        flex: 1,
        minWidth: 200,
        cellRenderer: (user: UserWithRole) => (
          <Text>
            {user.first_name} {user.last_name}
          </Text>
        ),
        valueGetter: (user: UserWithRole) =>
          `${user.first_name} ${user.last_name}`,
      },
      {
        field: "email",
        header: "Email",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "role_name",
        header: "Role",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "is_active",
        header: "Status",
        flex: 0.8,
        minWidth: 120,
        cellRenderer: (user: UserWithRole) => (
          <Text>{user.is_active ? "Active" : "Invite Sent"}</Text>
        ),
      },
      {
        field: "created_at",
        header: "Created At",
        flex: 1,
        minWidth: 130,
        cellRenderer: (user: UserWithRole) => (
          <Text>{new Date(user.created_at).toLocaleDateString()}</Text>
        ),
      },
      {
        field: "updated_at",
        header: "Updated At",
        flex: 1,
        minWidth: 130,
        cellRenderer: (user: UserWithRole) => (
          <Text>{new Date(user.updated_at).toLocaleDateString()}</Text>
        ),
      },
    ];

    // Column definitions for project users (with edit capabilities)
    const projectUsersColumnDefs: DataGridColumn<UserWithRole>[] = [
      {
        field: "first_name",
        header: "Name",
        flex: 1,
        minWidth: 200,
        cellRenderer: (user: UserWithRole) => (
          <Link
            to={`/org/${currentTenant?.id}/settings/users/${user.id}`}
            style={{
              color: "var(--accent-11)",
              textDecoration: "underline",
            }}
          >
            {user.first_name} {user.last_name}
          </Link>
        ),
        valueGetter: (user: UserWithRole) =>
          `${user.first_name} ${user.last_name}`,
      },
      {
        field: "email",
        header: "Email",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "role_name",
        header: "Role",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "auth_provider",
        header: "Auth",
        flex: 0.6,
        minWidth: 100,
        cellRenderer: (user: UserWithRole) => {
          if (!user.auth_provider)
            return (
              <Badge variant="soft" color="gray" size="1">
                Password
              </Badge>
            );
          const Icon = integrationIcons[user.auth_provider];
          const brand = integrationBrandColors[user.auth_provider];
          return (
            <Flex align="center" gap="1">
              {Icon && (
                <Box
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    backgroundColor: brand?.bg || "var(--gray-9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box style={{ transform: "scale(0.55)", display: "flex" }}>
                    <Icon />
                  </Box>
                </Box>
              )}
              <Text size="1" style={{ textTransform: "capitalize" }}>
                {user.auth_provider}
              </Text>
            </Flex>
          );
        },
      },
      {
        field: "is_active",
        header: "Status",
        flex: 0.8,
        minWidth: 120,
        cellRenderer: (user: UserWithRole) => (
          <Text>{user.is_active ? "Active" : "Invite Sent"}</Text>
        ),
      },
      {
        field: "referral_code",
        header: "Referral code",
        flex: 1,
        minWidth: 150,
        cellRenderer: (user: UserWithRole) => (
          <UserReferralCodeCell
            codes={referralCodesQuery.data?.[user.id] ?? null}
            isLoading={referralCodesQuery.isLoading}
          />
        ),
      },
      {
        field: "last_login_at",
        header: "Last Login",
        flex: 1.2,
        minWidth: 170,
        cellRenderer: (user: UserWithRole) => (
          <Text>
            {user.last_login_at
              ? new Date(user.last_login_at).toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "Never"}
          </Text>
        ),
      },
      {
        field: "created_at",
        header: "Created At",
        flex: 1,
        minWidth: 130,
        cellRenderer: (user: UserWithRole) => (
          <Text>{new Date(user.created_at).toLocaleDateString()}</Text>
        ),
      },
      {
        field: "updated_at",
        header: "Updated At",
        flex: 1,
        minWidth: 130,
        cellRenderer: (user: UserWithRole) => (
          <Text>{new Date(user.updated_at).toLocaleDateString()}</Text>
        ),
      },
    ];

    return (
      <PageTemplate
        title="User Management"
        description="Accounts, the roles that grant them access, and the groups they belong to"
      >
        <UserManagementTabs active="users" />
        <UserAccessSettings />
        {/* Anythink Users Section */}
        {centralUsersPage.total_items > 0 && (
          <Box mb="6">
            <Box mb="4">
              <Heading size="5" mb="2">
                MyAnythink Users ({centralUsersPage.total_items})
              </Heading>
              <Text size="2" color="gray">
                These users are centrally managed through MyAnythink (
                <a
                  href="https://my.anythink.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--accent-11)",
                    textDecoration: "underline",
                  }}
                >
                  my.anythink.cloud
                </a>
                ). All user information, roles, and permissions must be managed
                through the MyAnythink portal. No changes can be made from this
                interface.
              </Text>
            </Box>
            <DataGrid
              data={centralUsersPage}
              columns={withoutLocalSortAndFilter(centralUsersColumnDefs)}
              storageKey="central_users"
              currentPage={centralPage}
              currentPageSize={CENTRAL_PAGE_SIZE}
              pageSizeOptions={[CENTRAL_PAGE_SIZE]}
              rowSelection="none"
              height="auto"
              showPagination={centralUsersPage.total_pages > 1}
              onPageChange={setCentralPage}
              onPageSizeChange={() => {}}
            />
          </Box>
        )}

        {/* Separator if both sections exist */}
        {centralUsersPage.total_items > 0 && projectPage.total_items > 0 && (
          <Separator size="4" mb="6" />
        )}

        {/* Project Users Section */}
        <Box>
          <Flex justify="between" align="center" mb="4">
            <Box>
              <Heading size="5" mb="2">
                Project Users ({projectPage.total_items})
              </Heading>
              <Text size="2" color="gray">
                Local project users with full management capabilities including
                password resets and invitations.
              </Text>
            </Box>
            <Button onClick={() => setIsUsersDrawerOpen(true)} size="2">
              <Plus className="w-4 h-4" />
              Create Project User
            </Button>
          </Flex>

          {/* Toolbar — search, quick filters, sort. Only shown above project users. */}
          <Flex
            gap="3"
            align="center"
            mb="3"
            wrap="wrap"
            direction={{ initial: "column", sm: "row" }}
          >
            <Box style={{ minWidth: 220, flex: "0 1 280px" }}>
              <TextField.Root
                placeholder="Search name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              >
                <TextField.Slot>
                  <Search size={14} />
                </TextField.Slot>
                {searchTerm && (
                  <TextField.Slot>
                    <Box
                      onClick={() => setSearchTerm("")}
                      style={{ cursor: "pointer", display: "flex" }}
                    >
                      <X size={14} />
                    </Box>
                  </TextField.Slot>
                )}
              </TextField.Root>
            </Box>

            <Flex gap="2" align="center" wrap="wrap" style={{ flex: 1 }}>
              {CHIPS.map((chip) => {
                const active = activeChips.has(chip.key);
                return (
                  <Badge
                    key={chip.key}
                    color={active ? "green" : "gray"}
                    variant={active ? "solid" : "soft"}
                    size="2"
                    onClick={() => toggleChip(chip.key)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {chip.label}
                  </Badge>
                );
              })}
              {(activeChips.size > 0 || searchTerm) && (
                <Button
                  size="1"
                  variant="ghost"
                  color="gray"
                  onClick={clearAllFilters}
                >
                  Clear
                </Button>
              )}
            </Flex>

            <Select.Root
              value={sortKey}
              onValueChange={(v) => setSortKey(v as UserSort)}
            >
              <Select.Trigger placeholder="Sort by" />
              <Select.Content>
                <Select.Item value="newest">Newest</Select.Item>
                <Select.Item value="recently_active">Recently active</Select.Item>
                <Select.Item value="alphabetical">A–Z</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>

          {/* Bulk-action bar — only when something is selected. */}
          {selectedUsers.length > 0 && (
            <Flex
              justify="between"
              align="center"
              mb="3"
              p="2"
              style={{
                backgroundColor: "var(--accent-3)",
                border: "1px solid var(--accent-6)",
                borderRadius: "var(--radius-3)",
              }}
            >
              <Text size="2" weight="medium">
                {selectedUsers.length} selected
              </Text>
              <Flex gap="2">
                <Button
                  size="1"
                  variant="soft"
                  onClick={() => setSelectedUsers([])}
                >
                  Clear selection
                </Button>
                <Button
                  size="1"
                  color="red"
                  onClick={handleBulkDelete}
                  loading={bulkDeleteUsers.isPending}
                >
                  <Trash size={14} />
                  Delete {selectedUsers.length}
                </Button>
              </Flex>
            </Flex>
          )}

          {projectPage.total_items > 0 ? (
            <DataGrid
              data={projectPage}
              columns={withoutLocalSortAndFilter(projectUsersColumnDefs)}
              storageKey="project_users"
              topContent={
                projectQuery.isPlaceholderData && (
                  <Skeleton loading width="96px" height="16px" />
                )
              }
              currentPage={page}
              currentPageSize={pageSize}
              pageSizeOptions={PROJECT_PAGE_SIZES}
              rowSelection="multiple"
              onSelectionChanged={setSelectedUsers}
              height="auto"
              actions={(user: UserWithRole) => (
                <DropdownMenu
                  items={[
                    {
                      label: "View Profile",
                      icon: <UserRound size={16} />,
                      onClick: () => {
                        safeNavigate(
                          `/org/${currentTenant?.id}/settings/users/${user.id}`,
                        );
                      },
                    },
                    // Only show invite link for project users who have invite tokens
                    ...(user.invite_token
                      ? [
                          {
                            label: "Copy Invite Link",
                            icon: <Copy size={16} />,
                            onClick: () => {
                              const inviteUrl = `${window.location.origin}/org/${currentTenant?.id}/auth/invites/${user.invite_token}?org_id=${currentTenant?.id}`;
                              navigator.clipboard.writeText(inviteUrl);
                              toast.success("Invite link copied to clipboard");
                            },
                          },
                        ]
                      : []),
                    {
                      label: "Delete",
                      icon: (
                        <Trash size={16} style={{ color: "var(--red-9)" }} />
                      ),
                      onClick: async () => {
                        const confirmed = await confirm({
                          title: "Delete User",
                          message: `Are you sure you want to delete the user "${user.first_name} ${user.last_name}"?`,
                          confirmText: "Delete",
                          cancelText: "Cancel",
                          variant: "danger",
                        });

                        if (confirmed) {
                          deleteUser.mutate(user.id);
                        }
                      },
                    },
                  ]}
                />
              )}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          ) : (
            <Box
              p="4"
              style={{
                textAlign: "center",
                border: "1px dashed var(--gray-6)",
                borderRadius: "var(--radius-2)",
              }}
            >
              {hasFilters ? (
                <Flex direction="column" gap="2" align="center">
                  <Text color="gray" size="3">
                    No project users match the current filters.
                  </Text>
                  <Button size="1" variant="soft" onClick={clearAllFilters}>
                    Clear filters
                  </Button>
                </Flex>
              ) : (
                <Text color="gray" size="3">
                  No project users created yet. Create your first project user
                  using the button above.
                </Text>
              )}
            </Box>
          )}
        </Box>

        <SideDrawer
          isOpen={isUsersDrawerOpen}
          onClose={() => setIsUsersDrawerOpen(false)}
          title="Create New Project User"
        >
          <div className="p-4">
            <Formik
              initialValues={{
                first_name: "",
                last_name: "",
                email: "",
                password: "",
                role_id: 0,
              }}
              onSubmit={(values) => {
                createUser.mutate(values);
              }}
              validationSchema={Yup.object({
                first_name: Yup.string()
                  .required("This field is required.")
                  .matches(
                    /^[a-zA-Z0-9\s]+$/,
                    "Must contain only letters, numbers and spaces",
                  ),
                last_name: Yup.string()
                  .required("This field is required.")
                  .matches(
                    /^[a-zA-Z0-9\s]+$/,
                    "Must contain only letters, numbers and spaces",
                  ),
                email: Yup.string().email("Invalid email address").required(),
                role_id: Yup.number().required("This field is required."),
              })}
            >
              <Form className="space-y-4">
                <InputField name="first_name" label="First Name" required />
                <InputField name="last_name" label="Last Name" required />
                <InputField name="email" label="Email" required />
                <SelectField name="role_id" label="Role" required type="number">
                  {roles
                    ?.filter((role) => role.is_active && !role.is_administrator)
                    .map((role) => (
                      <SelectField.Item key={role.id} value={role.id}>
                        {role.name}
                      </SelectField.Item>
                    ))}
                </SelectField>

                <div className="pt-4">
                  <Button type="submit" loading={createUser.isPending}>
                    Create User
                  </Button>
                </div>
              </Form>
            </Formik>
          </div>
        </SideDrawer>
      </PageTemplate>
    );
  },
);

export default UsersPage;
